/**
 * staffApi — запросы кадров (OO/EO · HR) и разбор ответов (03.10, владелец: «ОО нанимает
 * и увольняет только операторов, остальных — ИО; увольнение само снимает будущую
 * работу; доступ новому — ссылкой в STAFF-бота»). Сервер: apps/api/src/routes/staff/.
 *   GET  /api/staff, ?status=fired      → ростер (roles[], has_tg; telegram_id — только ИО)
 *   POST /api/staff                     → найм {ok,id,invite} | 409 {existing}
 *   PUT  /api/staff/:id                 → статус / роли / телефон / имя, ник, дата найма
 *   GET  /api/staff/:id/fire-preview    → что снимется
 *   POST /api/staff/:id/fire {reason}   → {removed, games, notice}
 *   POST /api/staff/:id/restore         → {invite|null}
 *   POST /api/staff/:id/invite {unbind} → {invite}
 */
import { api } from "../api/client";
import { ApiException, type ApiList } from "../api/types";
import { sortRoles, type StaffRoleId } from "@ub/shared/staffRoles";

export type StaffStatus = "active" | "vacation" | "fired";

export interface StaffRow {
  id: string;
  name: string;
  nickname: string | null;
  phone: string | null;
  roles: StaffRoleId[];
  status: StaffStatus;
  hiredAt: string | null;
  firedAt: string | null;
  hasTg: boolean;
  gender: "м" | "ж" | null;
  /** 16.10: «не показывать в расстановке» — нет в дропдаунах доски ОО (служебные аккаунты) */
  boardHidden: boolean;
  /** 27.10: «видит всю историю клиентов» — чужие игры 1С и архив Квестгида в карточке клиента (ставят ОО и EO) */
  historyAll: boolean;
}

export interface Invite { url: string | null; expiresAt: string }
export interface HireInput { name: string; nickname: string; phone: string; gender: "м" | "ж" | null; roles: StaffRoleId[] }
/** canRestore — вправе ли спрашивающий вернуть найденного (сервер, canManage): ОО не видит «Вернуть» у ДК/ОО/ИО. */
export interface Duplicate { id: string; name: string; phone: string | null; status: string; firedAt: string | null; hiredAt: string | null; canRestore: boolean }
export type HireResult = { ok: true; id: string; invite: Invite | null } | { ok: false; error: string; existing: Duplicate | null };
export interface FireCounts { assignments: number; mc: number; hall_admin: number; dc_duty: number; extra_work: number }
export interface FireGame { bookingId: number; date: string; time: string; quest: string }
export interface FirePreview { counts: FireCounts; games: FireGame[]; blocked: string | null }
export interface FireResult { already: boolean; removed: FireCounts | null; games: FireGame[]; notice: string | null }

interface RawStaff {
  id: string; name?: string; nickname?: string | null; phone?: string | null; role?: string | null;
  roles?: string[]; status?: string; hired_at?: string | null; fired_at?: string | null;
  has_tg?: boolean; telegram_id?: number | null; gender?: string | null; board_hidden?: boolean; history_all?: boolean;
}
interface RawInvite { url: string | null; expires_at: string }
interface RawGame { booking_id: number; date: string; time: string; quest: string }

const inviteOf = (r: RawInvite | null | undefined): Invite | null => (r ? { url: r.url, expiresAt: r.expires_at } : null);
const gamesOf = (g: RawGame[] | undefined): FireGame[] =>
  (g ?? []).map((x) => ({ bookingId: x.booking_id, date: x.date, time: x.time, quest: x.quest }));

function rowOf(r: RawStaff): StaffRow {
  const st = r.status === "fired" || r.status === "vacation" ? r.status : "active";
  return {
    id: r.id,
    name: r.name || r.id,
    nickname: r.nickname || null,
    phone: r.phone || null,
    roles: sortRoles(r.roles?.length ? r.roles : [r.role]),
    status: st,
    hiredAt: r.hired_at || null,
    firedAt: orenStamp(r.fired_at),
    hasTg: r.has_tg ?? (r.telegram_id != null),
    gender: r.gender === "м" || r.gender === "ж" ? r.gender : null,
    boardHidden: r.board_hidden === true,
    historyAll: r.history_all === true,
  };
}

/** Текст ошибки API для инлайна/тоста. */
export function errText(e: unknown): string {
  if (e instanceof ApiException) return e.body?.error || `Ошибка ${e.status}`;
  return "Не удалось — проверьте связь";
}

/** Работающие + уволенные одним списком: по имени, без повторов. */
export async function fetchRoster(signal?: AbortSignal): Promise<StaffRow[]> {
  const [a, f] = await Promise.all([
    api<ApiList<RawStaff>>("/api/staff", { signal }),
    api<ApiList<RawStaff>>("/api/staff?status=fired", { signal }),
  ]);
  const seen = new Set<string>();
  return [...(a.results ?? []), ...(f.results ?? [])]
    .filter((r) => !seen.has(r.id) && !!seen.add(r.id))
    .map(rowOf)
    .sort((x, y) => x.name.localeCompare(y.name, "ru"));
}

export const putStaff = (id: string, body: Record<string, unknown>) =>
  api<{ ok: boolean }>(`/api/staff/${encodeURIComponent(id)}`, { method: "PUT", body });
/** 16.10: «не показывать в расстановке» — ОО и EO любому (routes/staff/board-hidden.ts). */
export const putBoardHidden = (id: string, body: Record<string, unknown>) =>
  api<{ ok: boolean }>(`/api/staff/${encodeURIComponent(id)}/board-hidden`, { method: "PUT", body, retries: 0 });
/** 27.10: «видит всю историю клиентов» {on} — ОО и EO (routes/staff/history-all.ts). */
export const putHistoryAll = (id: string, body: Record<string, unknown>) =>
  api<{ ok: boolean }>(`/api/staff/${encodeURIComponent(id)}/history-all`, { method: "PUT", body, retries: 0 });

export async function hireStaff(input: HireInput): Promise<HireResult> {
  const body = {
    name: input.name.trim(),
    ...(input.nickname.trim() ? { nickname: input.nickname.trim() } : {}),
    ...(phoneState(input.phone) === "ok" ? { phone: input.phone } : {}),
    ...(input.gender ? { gender: input.gender } : {}),
    roles: input.roles,
  };
  try {
    const r = await api<{ ok: boolean; id: string; invite: RawInvite | null }>("/api/staff", { method: "POST", body });
    return { ok: true, id: r.id, invite: inviteOf(r.invite) };
  } catch (e) {
    if (e instanceof ApiException && e.status === 409) {
      const ex = (e.body as { existing?: { id: string; name: string; phone: string | null; status: string; fired_at: string | null; hired_at?: string | null; can_restore?: boolean } } | null)?.existing;
      return { ok: false, error: errText(e), existing: ex ? { id: ex.id, name: ex.name, phone: ex.phone, status: ex.status, firedAt: orenStamp(ex.fired_at), hiredAt: orenStamp(ex.hired_at ?? null), canRestore: !!ex.can_restore } : null };
    }
    return { ok: false, error: errText(e), existing: null };
  }
}

export async function fetchFirePreview(id: string): Promise<FirePreview> {
  const r = await api<{ counts: FireCounts; games: RawGame[]; blocked: string | null }>(`/api/staff/${encodeURIComponent(id)}/fire-preview`);
  return { counts: r.counts, games: gamesOf(r.games), blocked: r.blocked };
}

export async function fireStaff(id: string, reason: string): Promise<FireResult> {
  const r = await api<{ already?: boolean; removed: FireCounts | null; games: RawGame[]; notice: string | null }>(
    `/api/staff/${encodeURIComponent(id)}/fire`, { method: "POST", body: reason ? { reason } : {} });
  return { already: !!r.already, removed: r.removed, games: gamesOf(r.games), notice: r.notice };
}

export async function restoreStaff(id: string): Promise<Invite | null> {
  const r = await api<{ invite: RawInvite | null }>(`/api/staff/${encodeURIComponent(id)}/restore`, { method: "POST", body: {} });
  return inviteOf(r.invite);
}

export async function issueInvite(id: string, unbind: boolean): Promise<Invite> {
  const r = await api<{ invite: RawInvite }>(`/api/staff/${encodeURIComponent(id)}/invite`, { method: "POST", body: unbind ? { unbind: true } : {} });
  return inviteOf(r.invite)!;
}

/**
 * Телефон из PhonePad (свободная строка): пусто, полный или неполный. 03.10 (ревью): неполный
 * раньше молча выпадал из найма — человек без телефона и без проверки дубля. +7/8 — ровно 11 цифр,
 * иначе 10–15 (зарубежный).
 */
export function phoneState(raw: string): "empty" | "ok" | "short" {
  const d = raw.replace(/\D/g, "");
  if (!d || d === "7" || d === "8") return "empty";
  if (/^[78]/.test(d)) return d.length === 11 ? "ok" : "short";
  return d.length >= 10 && d.length <= 15 ? "ok" : "short";
}

/** fired_at из базы — UTC (datetime('now')); день увольнения — по Оренбургу (+5), как ведомость. */
export function orenStamp(s: string | null | undefined): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?)$/.exec(s ?? "");
  const t = m ? Date.parse(`${m[1]}T${m[2]}Z`) : NaN;
  return Number.isFinite(t) ? new Date(t + 5 * 3_600_000).toISOString().slice(0, 19).replace("T", " ") : s || null;
}

/** «2026-06-12 08:26:23» → «12.06.26» (строка) или «12.06.2026» (full). */
export function fmtDay(s: string | null, full = false): string {
  const m = /(\d{4})-(\d{2})-(\d{2})/.exec(s ?? "");
  if (!m) return "";
  return `${m[3]}.${m[2]}.${full ? m[1] : m[1]!.slice(2)}`;
}
